import type { ReactElement, ReactNode } from "react";
import { useCallback, useEffect, useRef } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { withComponentErrorBoundary } from "utils/errorHandling";
import { ContactMetierActionsProvider, useSearch, useVirtualizedList } from "providers";
import { DeleteContactMetierDialog } from "components/Business/DeleteContactMetierDialog";
import { EditContactMetierDialog } from "components/Business/EditContactMetierDialog";
import { EditMultipleContactsMetiersDialog } from "components/Business/EditMultipleContactsMetiersDialog";
import type { ListRowProps } from "react-virtualized";
import { List, AutoSizer } from "react-virtualized";
import { ContactMetiersTableRow } from "./ContactMetiersTableRow";
import { ContactMetiersTableHead } from "./ContactMetiersTableHead";
import { LoadingScreen } from "components/Loading";
import type { ContactMetier, Poste } from "models";
import { useDebouncedCallback } from "use-debounce";

const OVERSCAN_ROW_COUNT = 10;

function getNoRowsComponent(): ReactElement {
  return (
    <Typography textAlign="center" sx={{ padding: 2 }}>
      Aucun Contact Métier ne correspond à vos critères de recherche
    </Typography>
  );
}

function rowRenderer(props: ListRowProps): ReactNode {
  return <ContactMetiersTableRow {...props} key={props.key} />;
}

function ContactMetiersTable(): ReactNode {
  const listRef = useRef<List>(null);

  const {
    filteredContactsMetiers,
    isLoading,
    deleteContactMetier,
    modifyContactMetier,
    modifyContactsMetiers,
  } = useSearch();
  const { cache } = useVirtualizedList();

  const resetListCellHeightsCache = useCallback(async (): Promise<void> => {
    // Retour rapide si la liste est vide ou non définie
    const rowCount = listRef.current?.props.rowCount ?? 0;
    if (rowCount < 1) {
      return;
    }
    await Promise.resolve();
    requestAnimationFrame(() => {
      cache.clearAll();
      listRef?.current?.measureAllRows?.();
      listRef?.current?.forceUpdateGrid?.();
    });
  }, [cache]);

  useEffect(() => {
    void resetListCellHeightsCache();
  }, [filteredContactsMetiers, resetListCellHeightsCache]);

  async function _deleteContactMetier(cm: ContactMetier): Promise<void> {
    await deleteContactMetier?.(cm);
    await resetListCellHeightsCache();
  }

  async function _updateContactMetier(cm: ContactMetier): Promise<void> {
    await modifyContactMetier?.(cm);
    await resetListCellHeightsCache();
  }

  const _onMultipleEdit = useCallback(
    async (contactsMetiersIdsToUpdate: Array<string>, nouveauPoste: Poste): Promise<void> => {
      modifyContactsMetiers?.(contactsMetiersIdsToUpdate, nouveauPoste);
      await resetListCellHeightsCache();
    },
    [modifyContactsMetiers, resetListCellHeightsCache]
  );

  const debouncedResetListCellHeightsCache = useDebouncedCallback(() => {
    void resetListCellHeightsCache();
  }, 100);

  // On recalcule la hauteur des lignes au resize de la fenêtre (debounced pour performances)
  useEffect(() => {
    window.addEventListener("resize", debouncedResetListCellHeightsCache);
    return () => {
      window.removeEventListener("resize", debouncedResetListCellHeightsCache);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ContactMetierActionsProvider
      onDelete={_deleteContactMetier}
      onEdit={_updateContactMetier}
      onMultipleEdit={_onMultipleEdit}
      onListDensityChange={resetListCellHeightsCache}>
      <Paper
        sx={{ mt: 1, height: "100%", display: "flex", flexFlow: "column", position: "relative" }}
        variant="outlined">
        <ContactMetiersTableHead />
        <Box sx={{ display: "relative", flex: "1 1 auto" }}>
          <AutoSizer>
            {({ width, height }) => (
              <List
                width={width}
                height={height}
                deferredMeasurementCache={cache}
                rowHeight={cache.rowHeight}
                rowRenderer={rowRenderer}
                rowCount={filteredContactsMetiers.length}
                overscanRowCount={OVERSCAN_ROW_COUNT}
                ref={listRef}
                noRowsRenderer={
                  filteredContactsMetiers.length === 0 && !isLoading
                    ? getNoRowsComponent
                    : undefined
                }
              />
            )}
          </AutoSizer>
          {isLoading && (
            <Box sx={{ position: "absolute", left: 0, right: 0, top: 0, pointerEvents: "none" }}>
              <LoadingScreen />
            </Box>
          )}
        </Box>
      </Paper>
      <DeleteContactMetierDialog />
      <EditContactMetierDialog />
      <EditMultipleContactsMetiersDialog />
    </ContactMetierActionsProvider>
  );
}

export const ContactMetiersTableWithErrorBoundary = withComponentErrorBoundary(ContactMetiersTable);
